 


<template>
  <AppLayout>
    <div class="transaction">
    <section id="transaction-products">
      <div class="container">
        
        <h6 style="margin-bottom: 0px;" class="fw-bold">Tender Management</h6>
        
        <!-- Stats Cards Section -->
        <div class="warehouse-inventory-card-wrapper">
          <div class="warehouse-inventory-card">
            <p>Draft <br>
              Tenders</p>
            <strong>{{ stats.draft || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Published <br>
              Tenders</p>
            <strong class="green">{{ stats.published || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Closed <br>
              Tenders</p>
            <strong class="red">{{ stats.closed || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Total <br>
              Submissions</p>
            <strong>{{ stats.totalSubmissions || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Pending <br>
              Reviews</p>
            <strong class="red">{{ stats.pendingReviews || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Active <br>
              Tenders</p>
            <strong class="green">{{ stats.active || 0 }}</strong>
          </div>
        </div> 

        <!-- Filter Section -->
        <div class="filter-search" style="margin-top: 0px;padding-left: 0px;">
          <div class="filter-search-header">
            <p>Filter</p>
            <div class="header-actions">
              <p class="clear" @click="clear">Clear</p>
            </div>
          </div>
          <div class="filter-search-body">
            <div class="form">
              <div class="row">
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <label for="search">Search</label>
                    <input type="text" class="form-control" v-model="searchParams.search" placeholder="Title, Reference..." @input="debouncedSearch" />
                  </div>
                </div>
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <label for="status">Status</label>
                    <select class="full" v-model="searchParams.status" @change="fetchTenders">
                      <option value="">All</option>
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="CLOSED">Closed</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <label for="category">Category</label>
                    <select class="full" v-model="searchParams.category" @change="fetchTenders">
                      <option value="">All</option>
                      <option v-for="category in tenderCategories" :key="category.id" :value="category.name">
                        {{ category.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <label for="dateFrom">Date From</label>
                    <input type="date" class="form-control" v-model="searchParams.dateFrom" @change="fetchTenders" />
                  </div>
                </div>
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <label for="dateTo">Date To</label>
                    <input type="date" class="form-control" v-model="searchParams.dateTo" @change="fetchTenders" />
                  </div>
                </div>
                <div class="col-md-2 align-self-end">
                  <div class="form-group">
                    <button class="btn btn-primary small" style="margin-top: 32px;" type="button" @click="fetchTenders">Search</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tenders Table -->
        <div class="products-list transaction-products-list" style="margin-top: 0px;">
          <h6 class="fw-bold">Tenders Table</h6>
          
          <!-- Skeleton loader for tenders table -->
          <SkeletonLoader v-if="loading" type="table" :rows="8" :columns="8" />
          
          <table v-else id="tendersTable" class="table table-hover">
            <thead style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">No.</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Reference</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Title</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Category</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Status</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Published</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Deadline</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Submissions</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Actions</th>
            </thead>
            <tbody v-if="!loading && tenders?.length > 0">
              <tr v-for="(tender, index) in tenders" :key="tender.id">
                <td>{{ index + 1 }}</td>
                <td>{{ tender.referenceNumber || tender.reference || 'N/A' }}</td>
                <td>{{ tender.title }}</td>
                <td>{{ (tender.category && (tender.category.name || tender.category)) || 'N/A' }}</td>
                <td>
                  <span :class="`badge badge-${tender.status === 'DRAFT' ? 'warning' : tender.status === 'PUBLISHED' ? 'success' : 'danger'}`">
                    {{ tender.status }}
                  </span>
                </td>
                <td>{{ formatDate(tender.publishDate || tender.publishedAt || tender.publishedDate) }}</td>
                <td>{{ formatDate(tender.submissionDeadline) }}</td>
                <td>{{ tender._count?.submissions ?? tender.submissionCount ?? 0 }}</td>
                <td>
                  <div class="btn-group" role="group">
                    <button
                      @click="viewTender(tender.id)"
                      class="btn btn-sm btn-outline-primary"
                      title="View Tender"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      @click="editTender(tender.id)"
                      class="btn btn-sm btn-outline-secondary"
                      title="Edit Tender"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      v-if="tender.status === 'PUBLISHED'"
                      @click="viewSubmissions(tender.id)"
                      class="btn btn-sm btn-outline-info"
                      title="View Submissions"
                    >
                      <i class="fas fa-file-alt"></i>
                    </button>
                    <button
                      v-if="tender.status === 'DRAFT'"
                      @click="publishTender(tender.id)"
                      class="btn btn-sm btn-outline-success"
                      title="Publish Tender"
                    >
                      <i class="fas fa-bullhorn"></i>
                    </button>
                    <button
                      v-if="tender.status === 'PUBLISHED'"
                      @click="copyTenderLink(tender.slug)"
                      class="btn btn-sm btn-outline-warning"
                      title="Copy Link"
                    >
                      <i class="fas fa-link"></i>
                    </button>
                    <button
                      @click="deleteTender(tender.id)"
                      class="btn btn-sm btn-outline-danger"
                      title="Delete Tender"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    </div>
  </AppLayout>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { API_BASE_URL } from '@/api/tender'
import { useRouter } from 'vue-router'
import AppLayout from '../../components/AppLayout.vue'
import SkeletonLoader from '../../components/SkeletonLoader'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import "datatables.net-buttons-bs5"
import "datatables.net-responsive-bs5"
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'


// Simple utility functions
const showToast = (message, type = 'info') => {
  console.log(`${type.toUpperCase()}: ${message}`)
  // You can replace this with your preferred toast library
}

const debounce = (func, delay) => {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

export default {
  name: 'TenderManagement',
  components: {
    AppLayout,
    SkeletonLoader
  },

  setup() {
    const router = useRouter()
    const loading = ref(false)
    const showCreateForm = ref(false)
    const selectedTender = ref(null)
    
    const stats = reactive({
      total: 0,
      draft: 0,
      published: 0,
      closed: 0,
    })
    
    const searchParams = reactive({
      search: '',
      category: '',
      status: '',
      page: 1,
      limit: 12,
    })
    
    const tenders = ref([])
    const totalTenders = ref(0)
    const totalPages = ref(0)
    
    const currentPage = computed(() => searchParams.page)
    
    // Use the token as a string, not parsed JSON
      const token = JSON.parse(localStorage.getItem('access_token'))
      // canonical API base
      const API_BASE = API_BASE_URL
    const fetchTenders = async () => {
      loading.value = true
      try {
        // Build query string from searchParams
        const params = { ...searchParams }
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })
        const query = new URLSearchParams(params).toString()
        const res = await fetch(`${API_BASE}/tender?${query}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        })
        if (!res.ok) {
          let msg = 'Failed to load tenders'
          try {
            const err = await res.json()
            msg = err?.error || err?.message || msg
          } catch (e) {/* ignore */}
          throw new Error(msg)
        }
        const data = await res.json()
        tenders.value = data.data || []
        totalTenders.value = data.total || 0
        totalPages.value = data.totalPages || 1
      } catch (error) {
        showToast('Error fetching tenders', 'error')
        console.error('Error fetching tenders:', error)
      } finally {
        loading.value = false
      }
    }
    
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_BASE}/tender/stats`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        })
        if (!res.ok) {
          let msg = 'Failed to load stats'
          try {
            const err = await res.json()
            msg = err?.error || err?.message || msg
          } catch (e) {/* ignore */}
          throw new Error(msg)
        }
        const data = await res.json()
        Object.assign(stats, data.data || data)
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    }
    
    const debouncedSearch = debounce(() => {
      searchParams.page = 1
      fetchTenders()
    }, 500)
    
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        searchParams.page = page
        fetchTenders()
      }
    }
    
    const viewTender = (id) => {
      router.push(`/admin/tenders/${id}`)
    }
    
    // No permission checks for edit, publish, delete, etc.
    const editTender = async (id) => {
      try {
        const res = await fetch(`${API_BASE}/tender/${id}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        })
        if (!res.ok) {
          let msg = 'Failed to load tender'
          try {
            const err = await res.json()
            msg = err?.error || err?.message || msg
          } catch (e) {/* ignore */}
          throw new Error(msg)
        }
        const data = await res.json()
        selectedTender.value = data.data || data
        showCreateForm.value = true
      } catch (error) {
        showToast('Error loading tender', 'error')
      }
    }
    
    const viewSubmissions = (id) => {
      router.push(`/admin/tenders/${id}/submissions`)
    }
    
    const publishTender = async (id) => {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to publish this tender? It will become publicly accessible.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, publish it!'
      })
      if (!result.isConfirmed) {
        return
      }
      try {
        const res = await fetch(`${API_BASE}/tender/${id}/publish`, {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        })
        if (!res.ok) {
          let msg = 'Failed to publish tender'
          try {
            const err = await res.json()
            msg = err?.error || err?.message || msg
          } catch (e) {/* ignore */}
          throw new Error(msg)
        }
        showToast('Tender published successfully', 'success')
        fetchTenders()
        fetchStats()
      } catch (error) {
        showToast('Error publishing tender', 'error')
      }
    }
    
    const copyTenderLink = async (slug) => {
      if (!slug) {
        showToast('No tender link available', 'error')
        return
      }
      const url = `${window.location.origin}/tenders/${slug}`
      try {
        await navigator.clipboard.writeText(url)
        alert('Link copied: ' + url)
        showToast('Tender link copied to clipboard', 'success')
      } catch (err) {
        showToast('Failed to copy link', 'error')
      }
    }
    
    const onTenderSaved = () => {
      showCreateForm.value = false
      selectedTender.value = null
      fetchTenders()
      fetchStats()
      showToast('Tender saved successfully', 'success')
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const d = new Date(dateString)
      if (isNaN(d.getTime())) return 'Invalid Date'
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    const deleteTender = async (id) => {
      try {
        const result = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you really want to delete this tender?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it!',
        })
        if (result.isConfirmed) {
          const res = await fetch(`${API_BASE}/tender/${id}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            }
          })
          if (!res.ok) {
            let msg = 'Failed to delete tender'
            try {
              const err = await res.json()
              msg = err?.error || err?.message || msg
            } catch (e) {/* ignore */}
            throw new Error(msg)
          }
          showToast('Tender deleted successfully', 'success')
          fetchTenders()
          fetchStats()
        }
      } catch (error) {
        showToast('Error deleting tender', 'error')
        console.error('Error deleting tender:', error)
      }
    }
    
    onMounted(() => {
      fetchTenders()
      fetchStats()
    })
    
    return {
      loading,
      showCreateForm,
      selectedTender,
      stats,
      searchParams,
      tenders,
      totalTenders,
      totalPages,
      currentPage,
      fetchTenders,
      debouncedSearch,
      goToPage,
      viewTender,
      editTender,
      viewSubmissions,
      publishTender,
      copyTenderLink,
      deleteTender,
      onTenderSaved,
      formatDate,
    }
  },
}
</script>

<style scoped>
/* Reuse styles from HomeTransactions.vue */
.tender-management {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.title-section h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.title-section p {
  color: #666;
  margin: 0;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-icon.draft { background: #f59e0b; }
.stat-icon.published { background: #10b981; }
.stat-icon.closed { background: #6b7280; }
.stat-icon.total { background: #3b82f6; }

.stat-content h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1a1a1a;
}

.stat-content p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.search-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-filters {
  display: flex;
  gap: 14px;
  align-items: center;
}

.search-input {
  position: relative;
  flex: 1;
}

.search-input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.search-input input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.search-input input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select {
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-width: 140px;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.loading {
  text-align: center;
  padding: 48px;
  color: #666;
}

.loading i {
  font-size: 24px;
  margin-bottom: 14px;
}

.empty-state {
  text-align: center;
  padding: 48px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 14px;
  color: #d1d5db;
}

.tender-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
}

.tender-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tender-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.tender-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.tender-ref {
  font-family: monospace;
  font-size: 12px;
  color: #666;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.tender-status {
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.tender-status.draft { background: #fef3c7; color: #92400e; }
.tender-status.published { background: #d1fae5; color: #065f46; }
.tender-status.closed { background: #f3f4f6; color: #374151; }
.tender-status.cancelled { background: #fee2e2; color: #991b1b; }

.tender-content h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a1a;
}

.tender-description {
  color: #666;
  font-size: 14px;
  margin: 0 0 14px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tender-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 12px;
}

.meta-item i {
  width: 14px;
}

.tender-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-outline {
  border: 1px solid #e5e7eb;
  color: #374151;
  background: white;
}

.btn-outline:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover {
  background: #059669;
}

.btn-info {
  background: #0ea5e9;
  color: white;
}

.btn-info:hover {
  background: #0284c7;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-top: 32px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }

  .search-filters {
    flex-direction: column;
    gap: 12px;
  }

  .tender-cards {
    grid-template-columns: 1fr;
  }

  .tender-actions {
    justify-content: space-between;
  }
}
</style>